import axios from 'axios';
import { baseURL } from './types';
import {
  GET_REVIEWS_REQUEST,
  GET_REVIEWS_SUCCESS,
  GET_REVIEWS_FAILURE,
  CREATE_REVIEW_REQUEST,
  CREATE_REVIEW_SUCCESS,
  CREATE_REVIEW_FAILURE,
  UPDATE_REVIEW_REQUEST,
  UPDATE_REVIEW_SUCCESS,
  UPDATE_REVIEW_FAILURE,
  DELETE_REVIEW_REQUEST,
  DELETE_REVIEW_SUCCESS,
  DELETE_REVIEW_FAILURE,
  GET_REVIEWS_BY_PATIENT_REQUEST,
  GET_REVIEWS_BY_PATIENT_SUCCESS,
  GET_REVIEWS_BY_PATIENT_FAILURE,
  GET_ALL_REVIEWS_ADMIN_SUCCESS
} from './types';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

const fetchAndStandardizeReviews = async (hospitalID, headers) => {
    const res = await axios.post(`${baseURL}/api/appointmentReviews/getall`, { hospitalID }, { headers });
    const reviewsArray = res.data.data || [];
    return reviewsArray.map(review => ({
      id: review.reviewId,
      PatientName:review.PatientName,

      patientId: review.PatientID,
      stars: parseInt(review.noofStars, 10) || 0,
      title: review.title || '',
      text: review.yourReview || '',
      type: 'review',
      createdAt: review.created_at,
    }));
};

const fetchAndStandardizeFeedback = async (hospitalID, headers) => {
    const res = await axios.post(`${baseURL}/api/appointmentReviewFeedback/getall`, { hospitalID }, { headers });
    const feedbackArray = res.data.data || [];
    return feedbackArray.map(feedback => ({
      id: feedback.id || feedback.feedbackId,         
      PatientID: feedback.PatientID || feedback.patientId, 
      hospitalID: feedback.hospitalID,
      stars: 0, // Not applicable for feedback
      title: '', // Not applicable for feedback
      text: feedback.feedback || '',
      type: 'feedback',
      reviewId: feedback.reviewId,
      createdAt: feedback.created_at,
    }));
};

export const getReviewsByDoctorId = (hospitalID) => async (dispatch) => {
    dispatch({ type: GET_REVIEWS_REQUEST });
    try {
      const headers = getAuthHeaders();
      const [reviewsResult, feedbackResult] = await Promise.allSettled([
          fetchAndStandardizeReviews(hospitalID, headers),
          fetchAndStandardizeFeedback(hospitalID, headers)
      ]);
  
      const reviews = reviewsResult.status === 'fulfilled' ? reviewsResult.value : [];
      const feedback = feedbackResult.status === 'fulfilled' ? feedbackResult.value : [];
      
      if (feedbackResult.status === 'rejected') {
          console.warn("Could not fetch review feedback:", feedbackResult.reason);
      }
  
      const combinedData = [...reviews, ...feedback];
      dispatch({ type: GET_REVIEWS_SUCCESS, payload: combinedData });
    } catch (err) {
      console.error("Error in getReviewsByDoctorId:", err);
      dispatch({ type: GET_REVIEWS_FAILURE, payload: err.response?.data || { message: 'An error occurred fetching review data' } });
    }
  };

export const createAppointmentReview = (reviewData) => async (dispatch) => {
  dispatch({ type: CREATE_REVIEW_REQUEST });
  try {
    const res = await axios.post(`${baseURL}/api/appointmentReviews/create`, reviewData, {
        headers: getAuthHeaders()
    });
    dispatch({ type: CREATE_REVIEW_SUCCESS, payload: res.data });
    return Promise.resolve(res.data);
  } catch (err) {
    dispatch({ type: CREATE_REVIEW_FAILURE, payload: err.response?.data || { message: 'An error occurred creating the review' } });
    return Promise.reject(err);
  }
};

export const createReviewFeedback = (feedbackData) => async (dispatch) => {
    dispatch({ type: CREATE_REVIEW_REQUEST });
    try {
        const res = await axios.post(`${baseURL}/api/appointmentReviewFeedback/create`, feedbackData, {
            headers: getAuthHeaders()
        });
        dispatch({ type: CREATE_REVIEW_SUCCESS, payload: res.data });
        return Promise.resolve(res.data);
    } catch (err) {
        dispatch({ type: CREATE_REVIEW_FAILURE, payload: err.response?.data || { message: 'An error occurred creating feedback' } });
        return Promise.reject(err);
    }
}

export const updateReview = (reviewId, reviewData) => async (dispatch) => {
    dispatch({ type: UPDATE_REVIEW_REQUEST });
    try {
        const payload = {
            reviewId: reviewId,
            ...reviewData
        };
        const res = await axios.patch(`${baseURL}/api/appointmentReviews/update`, payload, {
            headers: getAuthHeaders()
        });
        dispatch({ type: UPDATE_REVIEW_SUCCESS, payload: res.data });
        return Promise.resolve(res.data);
    } catch (err) {
        dispatch({ type: UPDATE_REVIEW_FAILURE, payload: err.response?.data || { message: 'An error occurred updating the review' } });
        return Promise.reject(err);
    }
}

// --- [FIXED] This action now constructs the full payload for updating feedback ---
export const updateReviewFeedback = (originalFeedback, newText) => async (dispatch) => {
    dispatch({ type: UPDATE_REVIEW_REQUEST });
    try {
        // Construct the payload exactly as the backend requires
        const payload = {
            id: originalFeedback.id,
            reviewId: originalFeedback.reviewId,
            PatientID: originalFeedback.PatientID,
            hospitalID: originalFeedback.hospitalID,
            feedback: newText,
        };
        const res = await axios.patch(`${baseURL}/api/appointmentReviewFeedback/update`, payload, {
            headers: getAuthHeaders()
        });
        dispatch({ type: UPDATE_REVIEW_SUCCESS, payload: res.data });
        return Promise.resolve(res.data);
    } catch (err) {
        dispatch({ type: UPDATE_REVIEW_FAILURE, payload: err.response?.data || { message: 'An error occurred updating feedback' } });
        return Promise.reject(err);
    }
}

export const deleteReviewFeedback = (feedbackItem) => async (dispatch) => {
    dispatch({ type: DELETE_REVIEW_REQUEST });
    try {
        const payload = {
            id: feedbackItem.id,
            reviewId: feedbackItem.reviewId
        };
        await axios.delete(`${baseURL}/api/appointmentReviewFeedback/delete`, {
            headers: getAuthHeaders(),
            data: payload 
        });
        dispatch({ type: DELETE_REVIEW_SUCCESS, payload: feedbackItem.id });
        return Promise.resolve();
    } catch (err) {
        dispatch({ type: DELETE_REVIEW_FAILURE, payload: err.response?.data || { message: 'An error occurred deleting feedback' } });
        return Promise.reject(err);
    }
}

export const deleteReviewAndFeedback = (reviewId) => async (dispatch, getState) => {
    dispatch({ type: DELETE_REVIEW_REQUEST });
    try {
        const headers = getAuthHeaders();
        const { reviews } = getState().reviews;
        const feedbackToDelete = reviews.filter(
            item => item.type === 'feedback' && item.reviewId === reviewId
        );
        const feedbackDeletePromises = feedbackToDelete.map(feedback =>
            axios.delete(`${baseURL}/api/appointmentReviewFeedback/delete`, {
                headers: headers,
                data: { id: feedback.id, reviewId: feedback.reviewId } 
            })
        );
        const reviewDeletePromise = axios.delete(`${baseURL}/api/appointmentReviews/delete`, {
            headers: headers,
            data: { reviewId: reviewId }
        });
        await Promise.all([reviewDeletePromise, ...feedbackDeletePromises]);
        dispatch({ type: DELETE_REVIEW_SUCCESS, payload: reviewId });
        return Promise.resolve();
    } catch (err) {
        console.error("Error in deleteReviewAndFeedback:", err);
        dispatch({ type: DELETE_REVIEW_FAILURE, payload: err.response?.data || { message: 'An error occurred during deletion.' } });
        return Promise.reject(err);
    }
};

export const getReviewsByLoggedInPatient = () => async (dispatch) => {
  dispatch({ type: GET_REVIEWS_BY_PATIENT_REQUEST });
  try {
    const headers = getAuthHeaders();
    const res = await axios.post(
      `${baseURL}/api/appointmentReviews/getall`,
      {},
      { headers: headers }
    );
    dispatch({ type: GET_REVIEWS_BY_PATIENT_SUCCESS, payload: res.data.data });
  } catch (err) {
    console.error("Error in getReviewsByLoggedInPatient:", err);
    dispatch({ type: GET_REVIEWS_BY_PATIENT_FAILURE, payload: err.response?.data || { message: 'An error occurred fetching your reviews' } });
  }
};


// --- [CORRECTED ACTION] ---
export const getAllReviewsForAdmin = () => async (dispatch) => {
  dispatch({ type: GET_REVIEWS_REQUEST });
  try {
    const headers = getAuthHeaders();
    // Pass an empty object to your 'getall' endpoints to signify "fetch all"
    const reviewPromise = axios.post(`${baseURL}/api/appointmentReviews/getall`, {}, { headers });
    const feedbackPromise = axios.post(`${baseURL}/api/appointmentReviewFeedback/getall`, {}, { headers });

    // ✅ Use Promise.allSettled to ensure both requests can complete independently
    const results = await Promise.allSettled([reviewPromise, feedbackPromise]);
    
    const reviewResult = results[0];
    const feedbackResult = results[1];

    // Safely extract data from successful promises, or use an empty array if one failed.
    const reviewData = reviewResult.status === 'fulfilled' ? reviewResult.value.data.data || [] : [];
    const feedbackData = feedbackResult.status === 'fulfilled' ? feedbackResult.value.data.data || [] : [];

    // Optional: Log an error if a request failed, for debugging purposes.
    if (reviewResult.status === 'rejected') {
      console.warn("Could not fetch all reviews:", reviewResult.reason);
    }
    if (feedbackResult.status === 'rejected') {
      console.warn("Could not fetch all feedback:", feedbackResult.reason);
    }

    // Now, standardize the data you received
    const reviews = reviewData.map(review => ({
      id: review.reviewId,
      patientId: review.PatientID,
      hospitalID: review.hospitalID, // Make sure your API returns this field for reviews
      stars: parseInt(review.noofStars, 10) || 0,
      title: review.title || '',
      text: review.yourReview || '',
      type: 'review',
      createdAt: review.created_at,
    }));
    
    const feedback = feedbackData.map(fb => ({
      id: fb.id || fb.feedbackId,         
      PatientID: fb.PatientID || fb.patientId, 
      hospitalID: fb.hospitalID,
      text: fb.feedback || '',
      type: 'feedback',
      reviewId: fb.reviewId,
      createdAt: fb.created_at,
    }));

    const combinedData = [...reviews, ...feedback];

    dispatch({ type: GET_ALL_REVIEWS_ADMIN_SUCCESS, payload: combinedData });

  } catch (err) {
    // This catch block will now only run for unexpected errors, not for API 404s.
    console.error("Critical Error in getAllReviewsForAdmin:", err);
    dispatch({ type: GET_REVIEWS_FAILURE, payload: err.response?.data || { message: 'An unexpected error occurred' } });
  }
};